import ServicePageTemplate from './ServicePageTemplate';

const mobileAppData = {
  metaTitle: 'Mobile App Development Company Dubai | iOS & Android Apps UAE',
  metaDescription: 'Build innovative mobile applications with Ascali. We provide iOS, Android, and cross-platform mobile app development services for startups and enterprises in Dubai, UAE.',
  bannerGradient: 'from-purple-600 via-purple-700 to-pink-600',
  category: 'Mobile Development',
  bannerTitle: 'Create Mobile Experiences That Users Love and Share',
  bannerSubtitle: 'Native and cross-platform mobile apps designed with precision UX and robust engineering — built to engage, retain, and grow your user base on iOS and Android.',
  icon: (
    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
  stats: [
    { value: '100+', label: 'Apps Published' },
    { value: '4.8★', label: 'Average App Rating' },
    { value: '5M+', label: 'End Users Reached' },
    { value: '50ms', label: 'Average Response Time' }
  ],
  overview: {
    title: 'Mobile Apps That Perform, Engage, and Scale',
    description: 'Ascali builds native and cross-platform mobile applications that deliver seamless experiences on every device. From consumer apps to enterprise mobility solutions, we combine beautiful UI design with rock-solid engineering to create apps your users will actually want to use every day.',
    highlights: [
      'Native iOS (Swift) and Android (Kotlin) development',
      'Cross-platform with React Native and Flutter for cost efficiency',
      'Offline-capable apps with local data sync',
      'App Store and Google Play optimization (ASO)',
      'Push notifications, analytics, and crash reporting built in'
    ]
  },
  deliverables: [
    'iOS & Android Applications',
    'App Store & Play Store Submission',
    'Backend API & Cloud Setup',
    'Push Notification System',
    'Analytics & Crash Reporting',
    'Performance Test Report',
    'Ongoing Maintenance Plan'
  ],
  benefits: [
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>,
      title: 'Native Performance',
      description: 'Smooth 60fps animations and instant responses with optimized code that leverages device-native capabilities for the best user experience.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" /></svg>,
      title: 'Intuitive UI/UX Design',
      description: 'Human-centered design following iOS HIG and Android Material Design guidelines ensures an experience users find natural and enjoyable.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" /></svg>,
      title: 'Offline-First Capability',
      description: 'Apps that work without an internet connection and sync intelligently when connectivity is restored — critical for field and enterprise apps.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>,
      title: 'Smart Push Notifications',
      description: 'Targeted, personalized push notifications with deep linking that bring users back and drive in-app engagement and conversions.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
      title: 'Built-In Analytics',
      description: 'Understand user behavior, track funnels, and measure feature adoption with integrated analytics from day one.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
      title: 'App Store Optimization',
      description: 'We optimize your listing with the right keywords, screenshots, and descriptions to maximize organic downloads and visibility.'
    }
  ],
  process: [
    { step: '01', title: 'Concept & Strategy', description: 'Market research, user persona definition, and feature scoping to validate your app idea before a single line of code is written.' },
    { step: '02', title: 'UI/UX Prototyping', description: 'Interactive wireframes and high-fidelity prototypes are tested with real users to validate flows before development begins.' },
    { step: '03', title: 'Agile Development', description: 'Two-week sprints with live TestFlight/Play Console builds keep you hands-on throughout the development process.' },
    { step: '04', title: 'Launch & Growth', description: 'App store submission, ASO optimization, crash monitoring setup, and a post-launch iteration plan for continuous improvement.' }
  ],
  technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Xamarin', 'Ionic', 'Expo', 'Firebase', 'AWS Amplify', 'Redux', 'MobX', 'GraphQL', 'REST APIs', 'SQLite', 'Realm', 'Fastlane'],
  cta: {
    title: 'Ready to Launch Your Mobile App?',
    subtitle: 'Turn your app idea into a polished, high-performing product that users love. Ascali takes your concept from strategy to the App Store.'
  },
  faqs: [
    {
      question: 'Should I build a native app or a cross-platform app?',
      answer: 'It depends on your goals. Native apps (Swift for iOS, Kotlin for Android) offer the best performance and access to device features, ideal for complex or hardware-intensive apps. Cross-platform frameworks like React Native or Flutter let you share 80–90% of code between iOS and Android, reducing cost and time-to-market while still delivering near-native performance. We help you choose the right approach based on your budget, timeline, and requirements.'
    },
    {
      question: 'How long does it take to develop a mobile app?',
      answer: 'A simple MVP typically takes 2–4 months. A feature-rich consumer or enterprise app usually takes 4–8 months. Timeline depends on complexity, number of integrations, and the number of platforms. We follow agile sprints so you see working builds every two weeks throughout development.'
    },
    {
      question: 'Will you handle the App Store and Google Play submission?',
      answer: 'Yes. We handle the entire submission process including creating developer accounts (if needed), preparing assets, writing optimized descriptions, configuring in-app purchases or subscriptions, and navigating the review process. We also help resolve any rejection issues.'
    },
    {
      question: 'Can you build a backend and admin panel for my app?',
      answer: 'Yes. Most mobile apps need a backend API and admin dashboard. We build scalable cloud-hosted backends using Node.js, Python, or other technologies, complete with an admin portal for managing users, content, and analytics.'
    },
    {
      question: 'How do you ensure the app is secure?',
      answer: 'We implement certificate pinning, encrypted local storage, secure token management (OAuth 2.0/JWT), input validation, and regular security scanning. We follow OWASP Mobile Top 10 guidelines throughout development.'
    },
    {
      question: 'Do you provide maintenance after the app launches?',
      answer: 'Yes. iOS and Android release regular OS updates that require app updates to maintain compatibility. We offer maintenance packages covering OS compatibility updates, security patches, performance improvements, and new feature development based on user feedback.'
    }
  ]
};

const MobileAppPage = () => <ServicePageTemplate service={mobileAppData} />;
export default MobileAppPage;
